import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";

import data from "../data/data.json";
import chartEnglishData from "../data/foodcharts.json";
import chartHindiData from "../data/foodchartshindi.json";

// Centralized navigation configuration
const NAV_LINKS = [
  { path: "/", label: "Home", end: true },
  { path: "/babyfoodcharts", label: "Charts" },
  { path: "/recipe-videos/baby-food-recipe-videos", label: "Videos" },
  { path: "/baby-food-recipes", label: "Blogs" },
  {
    path: "/parenting-blog/7-days-7-breakfast-recipes",
    label: "Parenting Blog",
  },
  { path: "/toys", label: "Toys" },
];

const EXTRA_MOBILE_LINKS = [
  { path: "/aboutus", label: "About Us" },
  { path: "/contactus", label: "Contact Us" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);

  // Toggle mobile drawer
  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  // Fetch Firebase Data
  useEffect(() => {
    let isMounted = true;

    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Fetch Food Videos
        const foodVideosSnapshot = await getDocs(collection(db, "FoodVideos"));
        const foodVideos = foodVideosSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        localStorage.setItem("recipe-videos", JSON.stringify(foodVideos));

        // Fetch Coupons
        const couponSnapshot = await getDocs(collection(db, "Coupons"));
        if (!couponSnapshot.empty) {
          const couponData = couponSnapshot.docs[0].data();
          if (isMounted) setCode(couponData?.code || "");
        }

        // Fetch Social Media Count
        const socialSnapshot = await getDocs(
          collection(db, "Social Media Count")
        );
        if (!socialSnapshot.empty) {
          localStorage.setItem(
            "socialmediacount",
            JSON.stringify(socialSnapshot.docs[0].data())
          );
        }
      } catch (error) {
        console.error("Error loading header data:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Save Static Data
    localStorage.setItem("main-page", JSON.stringify(data));
    localStorage.setItem(
      "food-chart-english",
      JSON.stringify(chartEnglishData)
    );
    localStorage.setItem("food-chart-hindi", JSON.stringify(chartHindiData));

    fetchAllData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs transition-all">
      {/* Top Promotional Coupon Banner */}
      {/* {code && (
        <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 text-white text-xs sm:text-sm py-2 px-4 text-center font-medium shadow-inner flex items-center justify-center gap-2">
          {loading ? (
            <span className="animate-pulse">
              Loading best offers for you...
            </span>
          ) : (
            <span>
              Use code{" "}
              <strong className="bg-white text-rose-600 px-2 py-0.5 rounded-md shadow-xs font-bold tracking-wider mx-1 uppercase">
                {code}
              </strong>{" "}
              for 50% OFF!
            </span>
          )}
        </div>
      )} */}

      {/* Main Navbar Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        {/* Brand Logo */}
        <Link
          to="/"
          onClick={closeMenu}
          className="flex items-center gap-2.5 group transition-transform active:scale-95"
        >
          <img
            src="../../images/logosvg12.svg"
            alt="MommyGK Logo"
            width={48}
            height={48}
            className="w-10 h-10 sm:w-12 sm:h-12 object-contain transition-transform group-hover:rotate-3"
            loading="eager"
          />
          <span className="text-2xl sm:text-3xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-800">
            MommyGK
          </span>
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {NAV_LINKS.map(({ path, label, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              className={({ isActive }) =>
                `relative px-3 lg:px-4 py-2 text-sm lg:text-base font-semibold rounded-lg transition-colors duration-200 ${
                  isActive
                    ? "text-indigo-600 bg-indigo-50/70"
                    : "text-slate-600 hover:text-indigo-600 hover:bg-slate-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {label}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-indigo-600 rounded-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Mobile Hamburger / Cross Toggle Button */}
        <button
          type="button"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-label="Toggle Navigation Menu"
          className="md:hidden relative p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-colors"
        >
          <div className="w-6 h-6 flex flex-col justify-center items-center gap-1.5">
            <span
              className={`w-5 h-0.5 bg-slate-700 rounded-full transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`w-5 h-0.5 bg-slate-700 rounded-full transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`w-5 h-0.5 bg-slate-700 rounded-full transition-all duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </div>
        </button>
      </div>

      {/* Mobile Backdrop & Drawer Menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 top-[80px] z-40 bg-slate-900/40 backdrop-blur-sm md:hidden animate-fade-in"
          onClick={closeMenu}
        />
      )}

      <div
        className={`fixed top-[80px] left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-xl transition-all duration-300 ease-in-out md:hidden overflow-hidden ${
          isMenuOpen
            ? "max-h-[calc(100vh-80px)] opacity-100 py-6"
            : "max-h-0 opacity-0 py-0 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col px-6 gap-2 max-h-[70vh] overflow-y-auto">
          {[...NAV_LINKS, ...EXTRA_MOBILE_LINKS].map(({ path, label, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              onClick={closeMenu}
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-lg font-semibold text-center transition-all ${
                  isActive
                    ? "text-indigo-600 bg-indigo-50 font-bold"
                    : "text-slate-700 hover:bg-slate-50 hover:text-indigo-600"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
